import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PublicFooter from "@/components/PublicFooter";
import { CheckCircle2, AlertCircle } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (!token) setError("Kein gültiger Reset-Link. Bitte fordere einen neuen an."); }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Passwort muss mindestens 8 Zeichen lang sein."); return; }
    if (pw !== pw2) { setError("Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Zurücksetzen. Token möglicherweise abgelaufen.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md bg-white rounded-2xl border border-neutral-200 shadow-sm p-8" data-testid="reset-password-page">
          <div className="h-12 w-12 rounded-xl bg-emerald-500 flex items-center justify-center font-display font-bold text-neutral-900 mb-6">AC</div>

          {success ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">Passwort gesetzt</h1>
              <p className="text-neutral-600">Du wirst gleich automatisch zum Login weitergeleitet…</p>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">Neues Passwort wählen</h1>
              <p className="text-neutral-500 mb-6">Wähle ein Passwort, das du dir merken kannst. Mindestens 8 Zeichen.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="rp1">Neues Passwort</Label>
                  <Input id="rp1" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="reset-pw" autoFocus />
                </div>
                <div>
                  <Label htmlFor="rp2">Passwort wiederholen</Label>
                  <Input id="rp2" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} data-testid="reset-pw2" />
                </div>
                {error && (
                  <div className="flex items-start gap-2 text-sm text-red-600" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
                  </div>
                )}
                <Button type="submit" className="w-full" disabled={loading || !token} data-testid="reset-submit">
                  {loading ? "Wird gesetzt…" : "Passwort setzen"}
                </Button>
                <p className="text-xs text-neutral-500 text-center">
                  <Link to="/forgot-password" className="underline">Neuen Reset-Link anfordern</Link> · <Link to="/login" className="underline">Zum Login</Link>
                </p>
              </form>
            </>
          )}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
